import { useState, useEffect } from 'react';
import { useAuth } from '../../../../../context/contexts';
import { icn_paypal, icn_square } from '../../../../../imports';
import { toast } from 'sonner';
import { squareOAuth } from '../../../../../api/payments/squareAuth';
import './Integrations.css';

const INTEGRATIONS = {
  square: {
    name: 'Square',
    description: 'Connect your Square account to process payments and manage transactions.',
    icon: icn_square
  },
  paypal: {
    name: 'PayPal',
    description: 'Accept PayPal payments and manage your PayPal business account.',
    icon: icn_paypal
  }
};

const Integrations = () => {
  const { currentUser } = useAuth();
  const [connectedApps, setConnectedApps] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check for OAuth callback parameters in URL
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (code && state) {
      handleOAuthCallback(code, state);
    }

    // Load existing integrations
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const squareIntegration = await squareOAuth.getIntegrationStatus();
      if (squareIntegration) {
        setConnectedApps(prev => ({
          ...prev,
          square: {
            status: 'active',
            connectedAt: squareIntegration.created_at
          }
        }));
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };

  const handleOAuthCallback = async (code, state) => {
    setIsLoading(true);
    try {
      await squareOAuth.handleOAuthCallback(code, state);
      await loadIntegrations();
      toast.success('Successfully connected to Square');
      // Remove OAuth parameters from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('OAuth callback error:', error);
      toast.error('Failed to complete Square connection');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (integration) => {
    if (integration === 'square') {
      try {
        const oauthUrl = squareOAuth.generateOAuthUrl();
        window.location.href = oauthUrl;
      } catch (error) {
        console.error('Error initiating Square OAuth:', error);
        toast.error('Failed to initiate Square connection');
      }
    }
  };

  const handleDisconnect = async (integration) => {
    if (integration === 'square') {
      setIsLoading(true);
      try {
        await squareOAuth.disconnectIntegration();
        setConnectedApps(prev => {
          const newApps = { ...prev };
          delete newApps[integration];
          return newApps;
        });
        toast.success('Successfully disconnected from Square');
      } catch (error) {
        console.error('Error disconnecting Square:', error);
        toast.error('Failed to disconnect from Square');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const filteredIntegrations = Object.entries(INTEGRATIONS).filter(([key, value]) =>
    value.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className='integrations-container'>
      <div className='integrations-header'>
        <h2>Integrations</h2>
        <p>Connect your payment processing accounts to manage transactions.</p>
      </div>

      <div className='search-bar'>
        <input
          type='text'
          placeholder='Search by integration name...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className='integrations-list'>
        {filteredIntegrations.map(([key, integration]) => (
          <div key={key} className='integration-item'>
            <div className='integration-info'>
              <div className='integration-icon'>
                <img src={integration.icon} alt={`${integration.name} icon`} />
              </div>
              <div className='integration-details'>
                <h3>{integration.name}</h3>
                <p>{integration.description}</p>
              </div>
            </div>
            <div className='integration-actions'>
              {connectedApps[key]?.status === 'active' ? (
                <>
                  <span className='status-badge'>Active</span>
                  <button
                    className='btn-disconnect'
                    onClick={() => handleDisconnect(key)}
                    disabled={isLoading}
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  className='btn-connect'
                  // onClick={() => handleConnect(key)}
                  disabled={isLoading}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Integrations;